/**
 * Opt-in retry for a single request, for flows that must survive a flaky
 * connection.
 *
 * Deliberately NOT an interceptor. `ErrorInterceptor` applies to every call in
 * the app and this app has a lot of them, many non-idempotent; widening its
 * policy would silently replay requests nobody has reviewed. Everything here
 * is applied by hand at a call site that has decided replaying is safe, which
 * also means the caller - not this module - owns the "is this POST
 * idempotent?" question.
 *
 * ## Not conflicting with ErrorInterceptor
 *
 * `ErrorInterceptor` already retries **502** three times for every HttpClient
 * request. Layering another retry on top of that would multiply, not add: 4
 * interceptor attempts inside 4 of ours is 16 requests for one click. So
 * `retryTransient()` (the HttpClient side) treats 502 as already handled and
 * gives up immediately, leaving that status to the interceptor. The
 * `fetch()`-based side has no interceptor above it at all, so it does handle
 * 502 itself. `INTERCEPTOR_OWNED_STATUSES` is the one place that split lives.
 */

import { MonoTypeOperatorFunction, throwError, timer } from 'rxjs';
import { retry } from 'rxjs/operators';

/**
 * Statuses that mean "no real answer, try again".
 *
 * - `0` - no HTTP response at all: dropped connection, offline, DNS blip.
 *   The most common failure on a flaky mobile connection and the one nothing
 *   in this app retried before.
 * - `408` / `504` - a timeout, at the client and at the gateway.
 * - `429` - rate limited. Mapbox returns this when a page fires its
 *   competitor-category searches in parallel; backing off is the documented
 *   way to handle it.
 * - `502` / `503` - upstream unreachable or restarting.
 *
 * `500` is deliberately absent: a generic server error is usually
 * deterministic, so retrying only triples the load and delays the error the
 * visitor needs to see.
 */
const TRANSIENT_STATUSES = new Set([0, 408, 429, 502, 503, 504]);

/** Retried by `ErrorInterceptor` already - see the note at the top. */
const INTERCEPTOR_OWNED_STATUSES = new Set([502]);

/** Attempts after the first one, so at most 4 requests for one call. */
export const MAX_TRANSIENT_RETRIES = 3;

const BASE_RETRY_DELAY_MS = 500;
const MAX_RETRY_DELAY_MS = 8_000;

/** A `fetch()` that came back with a non-OK status after every retry. */
export class RequestFailedError extends Error {
  constructor(
    public readonly status: number,
    public readonly url: string
  ) {
    super(`HTTP ${status} for ${url}`);
    this.name = 'RequestFailedError';
  }
}

export function isTransientStatus(status: number): boolean {
  return TRANSIENT_STATUSES.has(status);
}

/**
 * Exponential backoff with jitter. `attempt` is 1-based (1 = first retry).
 *
 * The jitter matters more here than usual: a sales-forecast page fires
 * several Mapbox category searches at once, so without it a rate limit would
 * make them all retry in lockstep and get rate limited together again.
 *
 * A server-supplied `Retry-After` wins over our own guess - that is the
 * server saying when it will be ready - but is still capped, so one bad
 * header cannot stall the page for ten minutes.
 */
export function retryDelayMs(
  attempt: number,
  retryAfterSeconds?: number | null
): number {
  if (retryAfterSeconds != null && retryAfterSeconds >= 0) {
    return Math.min(retryAfterSeconds * 1000, MAX_RETRY_DELAY_MS);
  }
  const exponential = BASE_RETRY_DELAY_MS * Math.pow(2, attempt - 1);
  // ±25% so parallel failures spread out instead of retrying in lockstep.
  // Capped after the jitter, not before, or the jitter would push the delay
  // 25% past the ceiling the cap exists to enforce.
  const jittered = exponential * (0.75 + Math.random() * 0.5);
  return Math.round(Math.min(jittered, MAX_RETRY_DELAY_MS));
}

/**
 * `Retry-After` is either a delay in seconds or an HTTP date. Returns seconds
 * from now, or null when the header is absent or unparseable.
 */
export function parseRetryAfter(
  value: string | null | undefined
): number | null {
  if (!value) return null;
  const seconds = Number(value);
  if (value.trim() !== '' && Number.isFinite(seconds)) {
    return Math.max(0, seconds);
  }
  const date = Date.parse(value);
  if (Number.isNaN(date)) return null;
  return Math.max(0, (date - Date.now()) / 1000);
}

/**
 * Retry a single HttpClient request on transient failures.
 *
 * Apply it per call, and only where replaying the request is harmless - for a
 * POST that means the caller has checked that creating the same thing twice
 * costs nothing. 502 is left to `ErrorInterceptor` (see the top of this file).
 *
 * `context` names the request in the retry breadcrumb. Retries are logged at
 * `warn`, not `error`: a retry that then succeeds is not an incident, but it
 * still belongs in the Sentry breadcrumb trail of a session that later fails.
 * Only the call site's final `console.error` becomes a Sentry issue.
 */
export function retryTransient<T>(
  context: string
): MonoTypeOperatorFunction<T> {
  return retry<T>({
    count: MAX_TRANSIENT_RETRIES,
    delay: (error: unknown, attempt: number) => {
      const status = statusOf(error);
      if (status == null || !isTransientStatus(status)) {
        return throwError(() => error);
      }
      if (INTERCEPTOR_OWNED_STATUSES.has(status)) {
        return throwError(() => error);
      }
      const wait = retryDelayMs(attempt, retryAfterOf(error));
      console.warn(
        `[retry] ${context}: HTTP ${status}, ` +
          `attempt ${attempt}/${MAX_TRANSIENT_RETRIES} in ${wait}ms`
      );
      return timer(wait);
    },
  });
}

/**
 * `fetch()` with the same policy, for the Mapbox calls that never touch
 * Angular's HttpClient (and so have no interceptor above them - this includes
 * 502, unlike `retryTransient`).
 *
 * Resolves only on an OK response. A non-OK status that survives every retry
 * is thrown as a `RequestFailedError` so the caller can read `.status`
 * instead of parsing a message; a network failure is rethrown as-is.
 */
export async function fetchWithRetry(
  url: string,
  context: string,
  init?: RequestInit
): Promise<Response> {
  for (let attempt = 0; ; attempt++) {
    let response: Response | null = null;
    let networkError: unknown = null;

    try {
      response = await fetch(url, init);
      if (response.ok) return response;
    } catch (error) {
      networkError = error;
    }

    // No response at all is status 0, the same convention HttpClient uses.
    const status = response ? response.status : 0;
    const outOfAttempts = attempt >= MAX_TRANSIENT_RETRIES;

    if (outOfAttempts || !isTransientStatus(status)) {
      if (networkError) throw networkError;
      throw new RequestFailedError(status, url);
    }

    const wait = retryDelayMs(
      attempt + 1,
      parseRetryAfter(response?.headers.get('Retry-After'))
    );
    console.warn(
      `[retry] ${context}: HTTP ${status}, ` +
        `attempt ${attempt + 1}/${MAX_TRANSIENT_RETRIES} in ${wait}ms`
    );
    await sleep(wait);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Status of an HttpErrorResponse, a RequestFailedError, or nothing. */
function statusOf(error: unknown): number | null {
  const status = (error as { status?: unknown })?.status;
  return typeof status === 'number' ? status : null;
}

function retryAfterOf(error: unknown): number | null {
  const headers = (error as { headers?: { get(name: string): string | null } })
    ?.headers;
  return parseRetryAfter(headers?.get?.('Retry-After'));
}
